import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import { es } from 'date-fns/locale';
import { formatDistanceToNowStrict } from 'date-fns';
import { format } from 'date-fns';
import 'react-day-picker/dist/style.css';
import { Appointment, Professional } from '../types';
import { Calendar, Clock, User, CheckCircle, XCircle, Filter, Plus } from 'lucide-react';
import '../styles/calendar.css';
import { clinicTime, clinicDateKey } from '../utils/clinicTime';

interface ImprovedAppointmentCalendarProps {
  appointments: Appointment[];
  isAdmin?: boolean;
  onStatusUpdate?: (appointmentId: string, status: string) => void;
  onCreateAppointment?: (date: Date, professionalId?: string) => void;
  professionals?: Professional[];
  showCreateButton?: boolean;
  className?: string;
}

const ImprovedAppointmentCalendar: React.FC<ImprovedAppointmentCalendarProps> = ({ 
  appointments, 
  isAdmin = false,
  onStatusUpdate,
  onCreateAppointment,
  professionals: propProfessionals,
  showCreateButton = true,
  className = ''
}) => {
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedProfessional, setSelectedProfessional] = useState<string>('all');
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');

  // Get unique professionals from appointments for admin filtering
  useEffect(() => {
    if (propProfessionals) {
      setProfessionals(propProfessionals);
    } else if (isAdmin && appointments.length > 0) {
      const uniqueProfessionals = appointments
        .filter(app => app.professional)
        .reduce((acc, app) => {
          if (app.professional && !acc.find(p => p.id === app.professional!.id)) {
            acc.push(app.professional);
          }
          return acc;
        }, [] as Professional[]);
      setProfessionals(uniqueProfessionals);
    }
  }, [appointments, isAdmin, propProfessionals]);

  // Filter appointments based on selected professional (for admin)
  const byProfessional = isAdmin && selectedProfessional !== 'all'
    ? appointments.filter(app => String(app.professional_id ?? app.professional?.id) === String(selectedProfessional))
    : appointments;

  // counts per status (for the filter chips), then the status filter itself
  const statusCounts = useMemo(() => {
    const c = { all: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 } as Record<string, number>;
    byProfessional.forEach(a => { c.all++; c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [byProfessional]);
  const filteredAppointments = statusFilter === 'all' ? byProfessional : byProfessional.filter(a => a.status === statusFilter);

  // Group appointments by date using start_time instead of date
  const appointmentsByDate = useMemo(() => {
    return filteredAppointments.reduce((acc, app) => {
      try {
        const date = clinicDateKey(app.start_time);
        if (!date) {
          console.warn('Invalid start_time for appointment:', app.id, app.start_time);
          return acc;
        }
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(app);
      } catch (error) {
        console.warn('Error processing appointment date:', app.id, app.start_time, error);
      }
      return acc;
    }, {} as Record<string, Appointment[]>);
  }, [filteredAppointments]);

  // When looking at pending requests, the one that expires first comes first; otherwise by time.
  const sortList = useCallback((list: Appointment[]) => [...list].sort((a, b) => {
    if (statusFilter === 'pending') {
      const ha = a.hold_expires_at ? new Date(a.hold_expires_at).getTime() : Infinity;
      const hb = b.hold_expires_at ? new Date(b.hold_expires_at).getTime() : Infinity;
      if (ha !== hb) return ha - hb;
    }
    return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
  }), [statusFilter]);

  // Get appointments for current month
  const currentMonthAppointments = useMemo(() => {
    const monthKey = format(currentDate, 'yyyy-MM');
    return sortList(filteredAppointments.filter(app => clinicDateKey(app.start_time).startsWith(monthKey)));
  }, [filteredAppointments, currentDate, sortList]);

  // Get appointments for selected day
  const selectedDayAppointments = useMemo(() => {
    if (!selectedDay) return [];
    const key = format(selectedDay, 'yyyy-MM-dd');
    return sortList(filteredAppointments.filter(app => clinicDateKey(app.start_time) === key));
  }, [filteredAppointments, selectedDay, sortList]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'confirmed':
        return <Calendar className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'confirmed':
        return 'Confirmado';
      case 'completed':
        return 'Completado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const modifiers = {
    hasAppointments: (date: Date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const apps = appointmentsByDate[dateStr] || [];
      return apps.length > 0;
    }
  };

  // Custom day component to add appointment count data attribute
  const CustomDay = ({ date }: { date: Date }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayAppointments = appointmentsByDate[dateStr] || [];
    const hasAppointments = dayAppointments.length > 0;
    
    return (
      <div 
        className={`relative w-full h-full flex items-center justify-center cursor-pointer ${hasAppointments ? 'has-appointments' : ''}`}
        data-count={hasAppointments ? dayAppointments.length.toString() : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelectedDay(date);
        }}
      >
        <span className="text-sm font-medium">{format(date, 'd')}</span>
        
        {/* Appointment indicators */}
        {hasAppointments && (
          <>
            {/* Small purple dot indicator */}
            <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
            
            {/* Appointment count badge */}
            <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
              {dayAppointments.length}
            </div>
          </>
        )}
      </div>
    );
  };

  const modifiersClassNames = {
    selected: 'selected-day bg-primary-600 text-white hover:bg-primary-700',
    hasAppointments: 'has-appointments',
  };

  const AppointmentCard = ({ appointment }: { appointment: Appointment }) => (
    <div className="card p-4 bg-white rounded-lg shadow border hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <h4 className="text-lg font-semibold text-gray-900">
              {appointment.patient_name}
            </h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(appointment.status)}`}>
              <div className="flex items-center gap-1">
                {getStatusIcon(appointment.status)}
                {getStatusText(appointment.status)}
              </div>
            </span>
            {appointment.source === 'public' && (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">Reserva online</span>
            )}
          </div>

          {isAdmin && appointment.status === 'pending' && appointment.hold_expires_at && (() => {
            const left = new Date(appointment.hold_expires_at).getTime() - Date.now();
            const urgent = left < 6 * 3600_000;
            return (
              <div className={`mb-3 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${urgent ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>
                <Clock className="h-4 w-4" />
                {left <= 0 ? 'Reserva vencida: se liberará en breve' : `Responder antes de ${format(new Date(appointment.hold_expires_at), "d MMM HH:mm", { locale: es })} (en ${formatDistanceToNowStrict(new Date(appointment.hold_expires_at), { locale: es })})`}
              </div>
            );
          })()}
          
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{clinicTime(appointment.start_time) && clinicTime(appointment.end_time) ? `${clinicTime(appointment.start_time)} - ${clinicTime(appointment.end_time)} hs` : 'Horario no disponible'}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{appointment.patient_email}</span>
              </div>
              {appointment.patient_phone && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{appointment.patient_phone}</span>
                </div>
              )}
            </div>
            
            {/* Show professional info only for admin users */}
            {isAdmin && !appointment.professional && appointment.professional_name && (
              <div className="space-y-1">
                <div className="font-medium text-gray-900">Profesional:</div>
                <div className="text-sm text-gray-600">{appointment.professional_name}</div>
                {appointment.specialty && <div className="text-sm text-gray-500">{appointment.specialty}</div>}
                {appointment.room_name && <div className="text-sm text-gray-500">{appointment.room_name}</div>}
              </div>
            )}
            {isAdmin && appointment.professional && (
              <div className="space-y-2">
                <div className="font-medium text-gray-900">Profesional:</div>
                <div className="text-sm text-gray-600">
                  {appointment.professional.user?.name || 'N/A'}
                </div>
                {appointment.professional.specialty && (
                  <div className="text-sm text-gray-500">
                    {appointment.professional.specialty}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {appointment.form_answers && appointment.form_answers.length > 0 && (
            <dl className="mt-3 rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm grid gap-1.5">
              {appointment.form_answers.map((a, i) => (
                <div key={i} className="flex flex-wrap gap-x-2">
                  <dt className="font-medium text-gray-700">{a.label}:</dt>
                  <dd className="text-gray-600">{a.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {isAdmin && appointment.status === 'pending' && onStatusUpdate && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" className="btn btn-md btn-primary" onClick={() => onStatusUpdate(appointment.id, 'confirmed')}>Confirmar turno</button>
              <button type="button" className="btn btn-md btn-outline" onClick={() => { if (window.confirm('¿Rechazar esta solicitud? Se avisará al paciente y se liberará el horario.')) onStatusUpdate(appointment.id, 'cancelled'); }}>Rechazar</button>
            </div>
          )}

          {appointment.notes && (
            <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700">
              <span className="font-medium">Notas:</span> {appointment.notes}
            </div>
          )}
        </div>
        
        {onStatusUpdate && isAdmin && (appointment.status === 'confirmed') && (
          <div className="ml-4 flex flex-col gap-2 shrink-0">
            <button type="button" className="btn btn-sm btn-outline" onClick={() => onStatusUpdate(appointment.id, 'completed')}>Marcar realizado</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => { if (window.confirm('¿Cancelar este turno confirmado? Se avisará al paciente y se liberará el horario.')) onStatusUpdate(appointment.id, 'cancelled'); }}>Cancelar turno</button>
          </div>
        )}
        {onStatusUpdate && !isAdmin && (
          <div className="ml-4">
            <select
              value={appointment.status}
              onChange={(e) => onStatusUpdate(appointment.id, e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="pending">Pendiente</option>
              <option value="confirmed">Confirmada</option>
              <option value="completed">Completada</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col lg:flex-row gap-6 w-full ${className}`}>
      {/* Left Column - Appointments List */}
      <div className="flex-1 lg:w-1/2">
        <div className="card p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedDay 
                  ? `Turnos para ${format(selectedDay, 'PPP', { locale: es })}`
                  : `Turnos de ${format(currentDate, 'MMMM yyyy', { locale: es })}`
                }
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {selectedDay 
                  ? `${selectedDayAppointments.length} turno${selectedDayAppointments.length !== 1 ? 's' : ''}`
                  : `${currentMonthAppointments.length} turno${currentMonthAppointments.length !== 1 ? 's' : ''} en el mes`
                }
              </p>
            </div>
            
            {showCreateButton && onCreateAppointment && (
              <button
                onClick={() => onCreateAppointment(selectedDay || new Date(), selectedProfessional !== 'all' ? selectedProfessional : undefined)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Turno
              </button>
            )}
          </div>

          {/* Professional Filter for Admin */}
          {isAdmin && professionals.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Filtrar por profesional:</span>
              </div>
              <select
                value={selectedProfessional}
                onChange={(e) => setSelectedProfessional(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">Todos los profesionales</option>
                {professionals.map(prof => (
                  <option key={prof.id} value={prof.id}>
                    {prof.user?.name || 'Profesional'} - {prof.specialty}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status filter: pending requests are the admin's main job, so they get a count and one click */}
          <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filtrar por estado">
            {([['all', 'Todos'], ['pending', 'Pendientes'], ['confirmed', 'Confirmados'], ['completed', 'Realizados'], ['cancelled', 'Cancelados']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                aria-pressed={statusFilter === key}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  statusFilter === key
                    ? 'bg-primary-600 text-white border-primary-600'
                    : key === 'pending' && statusCounts.pending > 0
                      ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {label} <span className="opacity-80">({statusCounts[key] || 0})</span>
              </button>
            ))}
          </div>
          {selectedDay && (
            <button type="button" className="mb-3 text-sm font-semibold text-primary-700 underline" onClick={() => setSelectedDay(null)}>
              ← Ver todo el mes
            </button>
          )}

          {/* Appointments List */}
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {(selectedDay ? selectedDayAppointments : currentMonthAppointments).length > 0 ? (
              (selectedDay ? selectedDayAppointments : currentMonthAppointments).map(appointment => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {statusFilter !== 'all'
                    ? `No hay turnos "${({ pending: 'pendientes', confirmed: 'confirmados', completed: 'realizados', cancelled: 'cancelados' } as Record<string, string>)[statusFilter]}" ${selectedDay ? 'este día' : 'este mes'}.`
                    : selectedDay ? 'No hay turnos para este día.' : 'No hay turnos programados para este mes.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column - Calendar */}
      <div className="lg:w-1/2">
        <div className="card p-0">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Calendario</h3>
            <p className="text-sm text-gray-600 mb-3">
              <span className="font-medium text-purple-600">💡 Haga clic en un día para ver los turnos específicos</span>
            </p>
            
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-bold">1</div>
                <span className="font-medium">Día con turnos</span>
              </div>
            </div>
          </div>
          
          <DayPicker
            mode="single"
            selected={selectedDay || undefined}
            onSelect={(date) => setSelectedDay(date || null)}
            month={currentDate}
            onMonthChange={setCurrentDate}
            locale={es}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            components={{
              Day: CustomDay
            }}
            className="p-4"
          />
        </div>
      </div>
    </div>
  );
};

export default ImprovedAppointmentCalendar;
